import React, {
  FC,
  useMemo,
} from 'react';
import { Form } from 'react-final-form';
import { EditorListing } from 'src/common/types/api/listings';
import Modal from 'src/common/components/Modal';
import BulkListingSelector from 'src/admin/common/components/SnoozeModal/BulkListingSelector';
import SnoozeSection from 'src/admin/common/components/SnoozeModal/SnoozeSection';
import getSnoozeStatus from 'src/common/utils/getSnoozeStatus';
import newTZDate, { formatDate } from 'src/common/utils/dateHelpers';
import { WizardFlowProvider } from 'src/common/components/WizardFlow/useWizardFlow';
import WizardFlowPage from 'src/common/components/WizardFlow/WizardFlowPage';
import { CALENDAR_SECTION, LISTING_SELECTOR_SECTION, SNOOZE_SECTION } from 'src/admin/common/components/SnoozeModal/constants';
import styles from './SnoozeModal.module.scss';
import CalendarSection from './CalendarSection';
import { SnoozeFormState } from './types';

export type SnoozeModalProps = {
  selectedListingIds: string[];
  listings: EditorListing[];
  individual?: boolean;
  submit?: (values: SnoozeFormState) => void;
  isOpen: boolean;
  closeModal: () => void;
};

/**
 * Initialize the values of the snooze modal.
 *
 * - Pre-populate the start date if all the selected listings have the same start date.
 * - Pre-populate the end date if all the selected listings have the same end date.
 * - Pre-populate the reason if all the selected listings have the same reason.
 *
 * If the snooze dates of any of the selected listings are in the past,
 * all the values will be unset.
 */
const getInitialValues = (
  listings: EditorListing[],
  selectedListingIds: string[],
): SnoozeFormState => {
  const selected = listings.filter(({ id }) => selectedListingIds.includes(id));
  const firstListing = selected.length
    ? selected.find(({ id }) => id === selectedListingIds[0]) ?? null
    : null;
  let startDate = firstListing && firstListing.snooze_start_date;
  let endDate = firstListing && firstListing.snooze_end_date;
  let reason = firstListing && firstListing.snooze_reason;

  const { snoozeActive, snoozeScheduled } = getSnoozeStatus(startDate, endDate);
  const snoozeInPast = !snoozeActive && !snoozeScheduled;

  for (const listing of selected) {
    if (listing.snooze_start_date !== startDate || snoozeInPast) {
      startDate = null;
    }
    if (listing.snooze_end_date !== endDate || snoozeInPast) {
      endDate = null;
    }
    if (listing.snooze_reason !== reason || snoozeInPast) {
      reason = null;
    }
  }
  return {
    snooze_enabled: true,
    boats: selectedListingIds,
    snooze_start_date: startDate && formatDate(newTZDate(startDate)),
    snooze_end_date: endDate && formatDate(newTZDate(endDate)),
    snooze_reason: reason,
  };
};

const SnoozeModal: FC<SnoozeModalProps> = ({
  selectedListingIds = [],
  listings: publishedListings = [],
  individual,
  isOpen,
  closeModal,
  submit,
}) => {
  const initialValues = useMemo(
    /** The mapping only needs to happen on modal open. All other listing changes
     * result in MinimalEditorListing
     */
    () => getInitialValues(publishedListings, selectedListingIds),
    [publishedListings, selectedListingIds],
  );

  // Needs to be an editor listing due to using extra fields e.g. first photo for the preview
  const individualListing = useMemo(() => {
    const firstId = selectedListingIds[0];
    if (individual) {
      return publishedListings.find(({ id }) => id === firstId) ?? null;
    }
    return null;
  }, [individual, publishedListings, selectedListingIds]);

  return (
    <Modal
      modalOpen={isOpen}
      closeModal={closeModal}
      label="snooze-modal"
      classNameModifier="snooze"
      preventScroll
    >
      <Form<SnoozeFormState>
        onSubmit={(values) => submit && submit(values)}
        initialValues={initialValues}
      >
        {({ handleSubmit }) => (
          <form
            method="POST"
            className={styles.form}
            onSubmit={handleSubmit}
          >
            <WizardFlowProvider>
              <WizardFlowPage name={SNOOZE_SECTION}>
                <SnoozeSection
                  individualListing={individualListing}
                  closeModal={closeModal}
                />
              </WizardFlowPage>
              <WizardFlowPage name={LISTING_SELECTOR_SECTION}>
                <BulkListingSelector />
              </WizardFlowPage>
              <WizardFlowPage name={CALENDAR_SECTION}>
                <CalendarSection />
              </WizardFlowPage>
            </WizardFlowProvider>
          </form>
        )}
      </Form>
    </Modal>
  );
};

export default SnoozeModal;
